import { Injectable } from '@angular/core'
import { Observable, from } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { HttpContext } from '@angular/common/http'
import { DrrHttpClient } from '../../../http/drr-http-client'
import { INCLUDE_AUTH_HEADERS, STRIP_EMPTY_BODY } from '../../../shared/services/http-context.tokens'
import { AuthResponse } from '../auth.types'
import {
  startWebAuthnAuthentication,
  startWebAuthnRegistration,
} from './webauthn.loader'
import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from './webauthn.utils'
import { AuthenticationResponseJSON } from './webauthn.types'

@Injectable({ providedIn: 'root' })
export class PasskeyService {
  constructor(private http: DrrHttpClient) {}

  isSupported(): Promise<boolean> {
    if (!browserSupportsWebAuthn()) {
      return Promise.resolve(false)
    }
    return platformAuthenticatorIsAvailable().then(
      (available) => available,
      () => false,
    )
  }

  fetchAuthOptions$(): Observable<{ optionsJSON: any; challengeId: string }> {
    return this.http.post<any>('bff/passkey/authentication/options', {}, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    }).pipe(
      map((response) => ({ optionsJSON: response, challengeId: response.challengeId })),
    )
  }

  verifyAuth$(assertion: AuthenticationResponseJSON, challengeId: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('bff/passkey/authentication/verify', {
      assertion,
      challengeId,
    }, {
      // The assertion is verified byte for byte on the server, so it has to reach it
      // exactly as the authenticator produced it — empty entries included.
      context: new HttpContext()
        .set(INCLUDE_AUTH_HEADERS, false)
        .set(STRIP_EMPTY_BODY, false),
    })
  }

  assert$(): Observable<AuthResponse> {
    return this.fetchAuthOptions$().pipe(
      switchMap(({ optionsJSON, challengeId }) =>
        from(startWebAuthnAuthentication({ optionsJSON })).pipe(
          switchMap((assertion) => this.verifyAuth$(assertion, challengeId)),
        ),
      ),
    )
  }

  assertWithOptions$(optionsJSON: any, challengeId: string): Observable<AuthResponse> {
    return from(startWebAuthnAuthentication({ optionsJSON })).pipe(
      switchMap((assertion) => this.verifyAuth$(assertion, challengeId)),
    )
  }

  register$(): Observable<void> {
    return this.http.post<any>('bff/passkey/registration/options', {}).pipe(
      switchMap((optionsJSON) => {
        const { challengeId } = optionsJSON
        return from(startWebAuthnRegistration({ optionsJSON })).pipe(
          switchMap((credential) =>
            this.http.post<void>('bff/passkey/registration/verify', {
              credential,
              challengeId,
            }, {
              // Same reason as verifyAuth$: the credential is verified as produced.
              context: new HttpContext().set(STRIP_EMPTY_BODY, false),
            }),
          ),
        )
      }),
    )
  }
}
